import { Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { BaseDialog } from '@/components/shared/base-dialog';
import { Icon } from '@/components/shared/icon';
import { useNotification } from '@/context/notification';
import { useCancelRefundRequest } from '@/data/subsidy/refund-requests/cancel';
import { useCancelResourceApproval } from '@/data/subsidy/resource-approvals/cancel';
import { RefundStatus } from '@/models/subsidy/status';
import { useRefundRequest } from '@/packages/subsidy/pages/refund-request/context';

interface CancelConfirmDialogProps {
  onClose: () => void;
}

const resourceApprovalCancelableStatuses: RefundStatus[] = [
  RefundStatus.ResourceApprovalPendingApproval,
  RefundStatus.ResourceApprovalRevisionRequired,
  RefundStatus.ResourceApprovalApproved,
];

export const CancelConfirmDialog = ({ onClose }: CancelConfirmDialogProps) => {
  const navigate = useNavigate();
  const { mutate: cancelRefundRequest, isPending: isCancelingRefundRequest } =
    useCancelRefundRequest();
  const {
    mutate: cancelResourceApproval,
    isPending: isCancelingResourceApproval,
  } = useCancelResourceApproval();
  const { resourceApproval, refundStatus } = useRefundRequest();
  const { toast } = useNotification();

  const canCancelRefundRequest =
    !!resourceApproval?.refund_request &&
    refundStatus !== RefundStatus.RefundRequestApproved;

  const canCancelResourceApproval =
    refundStatus && resourceApprovalCancelableStatuses.includes(refundStatus);

  const isCanceling = isCancelingRefundRequest || isCancelingResourceApproval;

  const onCancelSuccess = () => {
    toast({
      content: 'Solicitação cancelada com sucesso',
      type: 'success',
    });
    navigate('/meus-reembolsos');
  };

  const onCancelError = () => {
    toast({
      content: 'Erro ao cancelar solicitação',
      type: 'error',
    });
  };

  const handleCancel = () => {
    if (canCancelRefundRequest) {
      cancelRefundRequest(resourceApproval?.refund_request?.uuid, {
        onSuccess: onCancelSuccess,
        onError: onCancelError,
      });
      return;
    }

    if (canCancelResourceApproval && resourceApproval?.uuid) {
      cancelResourceApproval(resourceApproval?.uuid, {
        onSuccess: onCancelSuccess,
        onError: onCancelError,
      });
    }
  };

  return (
    <BaseDialog
      onClose={onClose}
      title="Tem certeza que deseja cancelar o pedido?"
      icon={<Icon name="siren-rounded" />}
      actionsComponent={
        <>
          <Button onClick={onClose}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            loading={isCanceling}
          >
            Cancelar solicitacao
          </Button>
        </>
      }
    >
      <Typography>
        Se cancelar, terá que abrir uma nova solicitação refazendo todo o
        processo e aguardando as aprovações necessárias.
      </Typography>
    </BaseDialog>
  );
};
